/**
 * Admin Portal API Client
 *
 * Wraps all calls to the platform admin REST API.
 * Token is read from the auth store (Cognito session).
 */

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.example.com/v1';

async function request(method, path, body) {
  const { fetchAuthSession } = await import('aws-amplify/auth');
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();

  const resp = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    ...(body !== undefined && { body: JSON.stringify(body) }),
  });

  const data = await resp.json();
  if (!resp.ok) {
    const err = new Error(data?.error?.message || `HTTP ${resp.status}`);
    err.code = data?.error?.code;
    err.status = resp.status;
    throw err;
  }
  return data;
}

// ─── Tenants ──────────────────────────────────────────────────────────────────

function listTenants(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return request('GET', `/admin/tenants${qs ? '?' + qs : ''}`);
}

function getTenant(tenantId) {
  return request('GET', `/admin/tenants/${tenantId}`);
}

function createTenant(payload) {
  return request('POST', '/admin/tenants', payload);
}

function updateTenantStatus(tenantId, status, reason) {
  return request('PUT', `/admin/tenants/${tenantId}/status`, { status, reason });
}

// ─── Feature Flags ────────────────────────────────────────────────────────────

function getTenantFeatures(tenantId) {
  return request('GET', `/admin/tenants/${tenantId}/features`);
}

function bulkUpdateTenantFeatures(tenantId, payload) {
  // payload can be { features: {...}, featureLevels: {...} } or just a flat features map (legacy)
  const body = payload.features || payload.featureLevels
    ? payload
    : { features: payload };
  return request('PUT', `/admin/tenants/${tenantId}/features`, body);
}

function setTenantFeature(tenantId, featureKey, enabled) {
  return request('PUT', `/admin/tenants/${tenantId}/features/${encodeURIComponent(featureKey)}`, { enabled });
}

function resetTenantFeatures(tenantId) {
  return request('POST', `/admin/tenants/${tenantId}/features/reset`);
}

// ─── Users ────────────────────────────────────────────────────────────────────

function listTenantUsers(tenantId) {
  return request('GET', `/admin/tenants/${tenantId}/users`);
}

function disableUser(tenantId, userId) {
  return request('PUT', `/admin/tenants/${tenantId}/users/${userId}/disable`);
}

// ─── API Keys ─────────────────────────────────────────────────────────────────

function listTenantApiKeys(tenantId) {
  return request('GET', `/admin/tenants/${tenantId}/api-keys`);
}

function revokeApiKey(tenantId, keyId) {
  return request('DELETE', `/admin/tenants/${tenantId}/api-keys/${keyId}`);
}

// ─── Usage & Monitoring ───────────────────────────────────────────────────────

// Accepts a { start, end } range (YYYY-MM-DD) or a legacy period string.
function periodQuery(params) {
  if (typeof params === 'string') { return `period=${params}`; }
  return new URLSearchParams(params || {}).toString();
}

function getTenantUsage(tenantId, params = 'month') {
  return request('GET', `/admin/tenants/${tenantId}/usage?${periodQuery(params)}`);
}

function getPlatformHealth() {
  return request('GET', '/admin/health');
}

function getPlatformMetrics(period = '24h') {
  return request('GET', `/admin/metrics?period=${period}`);
}

function getPlatformCosts(params = 'month') {
  return request('GET', `/admin/costs?${periodQuery(params)}`);
}

// ─── Platform Settings ────────────────────────────────────────────────────────

function getTrialSettings() {
  return request('GET', '/admin/settings/trial');
}

function updateTrialSettings(settings) {
  return request('PUT', '/admin/settings/trial', settings);
}

function getFeatureLimits() {
  return request('GET', '/admin/settings/feature-limits');
}

function updateFeatureLimits(overrides) {
  return request('PUT', '/admin/settings/feature-limits', overrides);
}

// Advanced settings — AWS API cache windows (Cost Explorer / monitoring).
function getCacheSettings() {
  return request('GET', '/admin/settings/cache');
}

function updateCacheSettings(settings) {
  return request('PUT', '/admin/settings/cache', settings);
}

// ─── WAF (dynamic on/off) ────────────────────────────────────────────────────
function getWafSettings() {
  return request('GET', '/admin/settings/waf');
}

function updateWafSettings(enabled) {
  return request('PUT', '/admin/settings/waf', { enabled });
}

// ─── Sales Settings ──────────────────────────────────────────────────────────

function getSalesSettings() {
  return request('GET', '/admin/settings/sales');
}

function updateSalesSettings(settings) {
  return request('PUT', '/admin/settings/sales', settings);
}

// ─── Add-On Configuration ───────────────────────────────────────────────────

function getAddonConfig() {
  return request('GET', '/admin/settings/addon-config');
}

function updateAddonConfig(config) {
  return request('PUT', '/admin/settings/addon-config', config);
}

// ─── Social Providers ────────────────────────────────────────────────────────

function getSocialProviders() {
  return request('GET', '/admin/settings/social-providers');
}

function updateSocialProvider(providerName, credentials) {
  return request('PUT', `/admin/settings/social-providers/${encodeURIComponent(providerName)}`, { credentials });
}

function deleteSocialProvider(providerName) {
  return request('DELETE', `/admin/settings/social-providers/${encodeURIComponent(providerName)}`);
}

// ─── Tenant Plan ──────────────────────────────────────────────────────────────

function updateTenantPlan(tenantId, payload) {
  return request('PUT', `/admin/tenants/${tenantId}/plan`, payload);
}

// ─── Platform Admin Users ────────────────────────────────────────────────────

function listAdminUsers() {
  return request('GET', '/admin/users');
}

function createAdminUser(email, role) {
  return request('POST', '/admin/users', { email, role });
}

function updateAdminUserRole(userId, role) {
  return request('PUT', `/admin/users/${encodeURIComponent(userId)}/role`, { role });
}

function disableAdminUser(userId) {
  return request('PUT', `/admin/users/${encodeURIComponent(userId)}/disable`);
}

function enableAdminUser(userId) {
  return request('PUT', `/admin/users/${encodeURIComponent(userId)}/enable`);
}

export const adminApi = {
  // Tenants
  listTenants,
  getTenant,
  createTenant,
  updateTenantStatus,
  // Features
  getTenantFeatures,
  bulkUpdateTenantFeatures,
  setTenantFeature,
  resetTenantFeatures,
  // Tenant Users
  listTenantUsers,
  disableUser,
  // API Keys
  listTenantApiKeys,
  revokeApiKey,
  // Usage
  getTenantUsage,
  getPlatformHealth,
  getPlatformMetrics,
  getPlatformCosts,
  // Settings
  getTrialSettings,
  updateTrialSettings,
  getFeatureLimits,
  updateFeatureLimits,
  getCacheSettings,
  updateCacheSettings,
  getWafSettings,
  updateWafSettings,
  // Sales Settings
  getSalesSettings,
  updateSalesSettings,
  // Social Providers
  getSocialProviders,
  updateSocialProvider,
  deleteSocialProvider,
  // Plan management
  updateTenantPlan,
  // Add-On Config
  getAddonConfig,
  updateAddonConfig,
  // Platform Admin Users
  listAdminUsers,
  createAdminUser,
  updateAdminUserRole,
  disableAdminUser,
  enableAdminUser,
};
